import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import hero720 from '../assets/hero.mp4';
import hero1080 from '../assets/hero-1080.mp4';
import heroPoster from '../assets/hero-poster.jpg';

type NetworkInfo = { saveData?: boolean; effectiveType?: string };

/** 1080p on desktop-sized screens with a decent connection; the light 720p file everywhere else. */
const pickSource = (): string => {
  if (typeof window === 'undefined') return hero720;
  const conn = (navigator as Navigator & { connection?: NetworkInfo }).connection;
  const slow = !!conn && (conn.saveData || /(^|-)(2g|3g)$/.test(conn.effectiveType ?? ''));
  return window.matchMedia('(min-width: 768px)').matches && !slow ? hero1080 : hero720;
};

const MEDIA_CLASS =
  'absolute inset-0 w-full h-full object-cover object-[50%_25%] md:object-center md:scale-[1.03]';
const MEDIA_STYLE = { filter: 'brightness(0.72) contrast(1.06)' };

/**
 * Full-bleed hero background. The poster is shown immediately; the video fades in
 * over it once it is actually playing, with a small loader in between.
 */
const HeroVideo = () => {
  const [src] = useState(pickSource);
  const [ready, setReady] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // If playback never starts (autoplay blocked, very slow network), stop showing the loader.
  useEffect(() => {
    const t = window.setTimeout(() => setGaveUp(true), 15000);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <>
      <img
        src={heroPoster}
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        decoding="async"
        className={MEDIA_CLASS}
        style={MEDIA_STYLE}
      />

      <video
        ref={videoRef}
        src={src}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        className={`${MEDIA_CLASS} transition-opacity duration-[1600ms] ease-out ${
          ready ? 'opacity-100' : 'opacity-0'
        }`}
        style={MEDIA_STYLE}
        onLoadedMetadata={(e) => {
          // Autoplay blocked (e.g. low-power mode): keep the poster and drop the loader
          e.currentTarget.play().catch(() => setGaveUp(true));
        }}
        onPlaying={() => setReady(true)}
      />

      {!ready && !gaveUp && (
        <div
          role="status"
          aria-label="Loading video"
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 pointer-events-none"
        >
          <span className="label-sm text-white/80">Loading</span>
          <span className="relative block w-24 h-px bg-white/25 overflow-hidden">
            <motion.span
              className="absolute inset-y-0 left-0 w-1/2 bg-white/90"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            />
          </span>
        </div>
      )}
    </>
  );
};

export default HeroVideo;
